import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { deleteUserByEmail } from "@/utils/adminUserDeletion";
import { UserX } from "lucide-react";

export function AdminUserDeleteCard() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const onDelete = async () => {
    if (!email.trim()) {
      toast({ title: "Invalid email", description: "Please enter a valid email", variant: "destructive" });
      return;
    }

    const confirmed = confirm(`Delete user ${email.trim()}?\n\nThis cannot be undone.`);
    if (!confirmed) return;

    setLoading(true);
    try {
      const res = await deleteUserByEmail(email.trim());
      toast({ title: "User deleted", description: res?.message || `Deleted ${email.trim()}` });
      setEmail("");
    } catch (err: any) {
      console.error("Delete user error:", err);
      toast({ title: "Error", description: err?.message || "Failed to delete user", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Delete User (Admin)</CardTitle>
        <CardDescription>Enter the user's email to permanently remove their account</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            aria-label="User email"
          />
          <Button onClick={onDelete} disabled={loading || !email.trim()} variant="destructive">
            <UserX className="h-4 w-4 mr-2" />
            {loading ? "Deleting..." : "Delete User"}
          </Button>
        </div>
        <div className="text-sm text-muted-foreground">Action is irreversible. Ensure you have the correct email.</div>
      </CardContent>
    </Card>
  );
}
